// packages/chatbot/src/hooks/controller/useTransientNewChatMemory.ts

import { useEffect } from 'react';
import type { UIMessage, ChatMemoryScope } from '../../types';
import { NEW_CHAT_ID } from '../../utils/constants';
import { saveChatMemory, loadChatMemory } from '../../utils/memory';
import { wasGreeted, markGreeted } from '../../utils/storage';

type Params = {
  scope?: ChatMemoryScope;
  agentId: string | null;
  authMode: 'widget' | 'test';
  activeConversationId: string | null;
  messages: UIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>;
  initialMessage?: { content: string };
};

/**
 * Handles the "new chat" transient message array and greeting idempotency
 * when activeConversationId === NEW_CHAT_ID.
 * - Loads saved draft transcript (session/local storage) for NEW_CHAT_ID
 * - Saves transcript whenever it changes (and is non-empty)
 * - Injects greeting once for NEW_CHAT_ID per scope+agent+mode
 */
export function useTransientNewChatMemory({
  scope,
  agentId,
  authMode,
  activeConversationId,
  messages,
  setMessages,
  initialMessage,
}: Params) {
  const isNewChat = activeConversationId === NEW_CHAT_ID;

  // Load transient transcript for NEW_CHAT_ID
  useEffect(() => {
    if (!scope) return;
    if (!isNewChat) return;

    const loaded = loadChatMemory(scope, agentId || undefined);
    if (loaded && loaded.length > 0) {
      setMessages(loaded);
    }
    // Only on entering NEW_CHAT_ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, agentId, isNewChat]);

  // Save transient transcript when NEW_CHAT_ID and messages are non-empty
  useEffect(() => {
    if (!scope) return;
    if (!isNewChat) return;
    if (!messages || messages.length === 0) return;

    saveChatMemory(messages, scope, agentId || undefined);
  }, [scope, agentId, isNewChat, messages]);

  // Inject greeting exactly once for NEW_CHAT_ID per (mode, scope, agent)
  useEffect(() => {
    if (!initialMessage?.content) return;
    if (!isNewChat) return;
    if (messages.length > 0) return; // do not inject if user already typed something or draft exists

    const modeTag = authMode === 'widget' ? 'widget' : 'test';
    const greeted = wasGreeted(scope || 'site', agentId || null, modeTag);
    if (greeted) return;

    setMessages((prev) => {
      if (prev.length > 0) return prev;
      const greeting: UIMessage = {
        id: `greet-${Date.now()}`,
        type: 'assistant',
        content: initialMessage.content,
        timestamp: new Date().toISOString(),
      } as UIMessage;
      return [...prev, greeting];
    });
    markGreeted(scope || 'site', agentId || null, modeTag);
  }, [initialMessage?.content, isNewChat, messages.length, scope, agentId, authMode, setMessages]);
}
